import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { authClient } from "./lib/auth-client";

export interface AuthUser {
  email: string;
  id: string;
  image: string | null;
  name: string;
}

type AuthStatus =
  | "loading"
  | "authorized"
  | "signed-out"
  | "forbidden"
  | "not-configured";

interface AuthContextValue {
  signOut(): Promise<void>;
  status: AuthStatus;
  user: AuthUser | null;
}

interface MeResponse {
  authenticated: boolean;
  authorized: boolean;
  configured: boolean;
  user?: AuthUser | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch("/api/auth/me", { credentials: "include" });
      const body = (await response.json()) as MeResponse;
      setUser(body.user ?? null);
      // The development server already leaves dashboard API reads unguarded when
      // auth is not configured. Mirror that boundary in the client so the local
      // demo and replay workspace remain usable without production credentials.
      if (!body.configured && import.meta.env.DEV) setStatus("authorized");
      else if (!body.configured) setStatus("not-configured");
      else if (!body.authenticated) setStatus("signed-out");
      else if (!body.authorized) setStatus("forbidden");
      else setStatus("authorized");
    } catch {
      setUser(null);
      setStatus("not-configured");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    await authClient.signOut();
    setUser(null);
    setStatus("signed-out");
  }, []);

  const value = useMemo(
    () => ({ signOut, status, user }),
    [signOut, status, user],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
};
